import {
  type ClassificationDefinition,
  type InstallationQuotationStatus,
  type InstallationType,
  type NbaType,
  type OpportunitiesSearchFilters,
  type OpportunityType,
  type Order,
  ProjectFilterDefault,
  type WorkOrderStatus,
  type WorkOrderType,
  type hfb,
} from "@repo/api";
import { assertUnreachable, toggleRecordArrayValue } from "@repo/utils";
import { type Draft, produce } from "immer";
import { type ActionType, createAction, getType } from "typesafe-actions";

export type ProjectFilterActions = typeof projectFilterActions;
export const projectFilterActions = {
  clearAll: createAction("CLEAR_ALL")(),
  setAllFilters:
    createAction("SEL_ALL_FILTERS")<Partial<OpportunitiesSearchFilters>>(),
  setTerm: createAction("SET_TERM")<string | undefined>(),
  setAmount: createAction("SET_VALUE")<number[] | undefined>(),
  setCreatedDate: createAction("SET_CREATED_DATE")<(Date | undefined)[]>(),
  setUpdatedDate: createAction("SET_UPDATED_DATE")<(Date | undefined)[]>(),
  setStatus: createAction("SET_STATUS")<string>(),
  setOpportunityType: createAction("SET_OPPORTUNITY_TYPE")<OpportunityType>(),
  setClassification:
    createAction("SET_CLASSIFICATION")<ClassificationDefinition>(),
  setDepartment: createAction("SET_DEPARTMENT")<hfb>(),
  setBusinessUnit: createAction("SET_BU")<string>(),
  setCustomerType:
    createAction("SET_CUSTOMER_TYPE")<Customer["customerType"]>(),
  setServices: createAction("SET_SERVICES")<number>(),
  setFollowUpConsent: createAction("SET_FOLLOWUP_CONSENT")<
    OpportunitiesSearchFilters["followUpConsentFilter"] | undefined
  >(),
  setPromotionalConsent: createAction("SET_PROMOTIONAL_CONSENT")<
    string | undefined
  >(),
  setTeam: createAction("SET_ALL_TEAMS")<number | undefined>(),
  setAssignedToTeam: createAction("SET_ASSIGNED_TO_TEAM")<boolean>(),
  setCoworkers: createAction("SET_COWORKERS")<string[] | undefined>(),
  setTags: createAction("SET_TAGS")<string>(),
  setNbaTypes: createAction("SET_NBA_TYPES")<NbaType>(),
  setOrderStatus: createAction("SET_ORDER_STATUS")<Order["orderStatus"]>(),
  setCustomerLocales: createAction("SET_CUSTOMER_LOCALES")<string>(),
  setOrderMethod: createAction("SET_ORDER_METHOD")<string>(),
  setBokaServices: createAction("SET_BOKA_SERVICES")<string>(),
  setInstallationQuotationStatus: createAction(
    "SET_INSTALLATION_QUOTATION_STATUS"
  )<InstallationQuotationStatus>(),
  setInstallationQuotationExists: createAction(
    "SET_INSTALLATION_QUOTATION_EXISTS"
  )<boolean | undefined>(),
  setWorkOrderStatus: createAction("SET_WORK_ORDER_STATUS")<WorkOrderStatus>(),
  setWorkOrderType: createAction("SET_WORK_ORDER_TYPE")<WorkOrderType>(),
  setIsNoActionPlanned: createAction("SET_IS_NO_ACTION_PLANNED")<boolean>(),
  setProjectIds: createAction("SET_PROJECT_IDS")<string[]>(),
  setInstallationType: createAction(
    "SET_INSTALLATION_TYPE"
  )<InstallationType>(),
  setInstallationDate: createAction("SET_INSTALLATION_DATE")<
    (Date | undefined)[]
  >(),
  setIsColdProject: createAction("SET_IS_COLD_PROJECT")<boolean>(),
  setStageFilter: createAction("SET_STAGE_FILTER")<string>(),
  setWaitingList: createAction("SET_WAITING_LIST_EXISTS")<
    boolean | undefined
  >(),
  setExpectedPurchaseDate: createAction("SET_EXPECTED_PURCHASE_DATE")<
    (Date | undefined)[]
  >(),
  setBudget: createAction("SET_BUDGET")<number[] | undefined>(),
};

interface ProjectFilterState {
  filters: OpportunitiesSearchFilters;
  isLoaded: boolean;
}

export function projectFilterReducer(
  state: ProjectFilterState,
  a: ActionType<ProjectFilterActions>
) {
  return produce(state, (draft: Draft<ProjectFilterState>) => {
    // Reset projectIds on every action except setting projectIds
    // This filter is only for Ai Project Summary
    draft.filters.projectIds = undefined;

    switch (a.type) {
      case getType(projectFilterActions.clearAll):
        draft.filters = {
          ...ProjectFilterDefault,
          teamFilter: state.filters.teamFilter,
          coworkerFilter: state.filters.coworkerFilter,
        };
        return;
      case getType(projectFilterActions.setTerm):
        draft.filters.term = a.payload;
        return;
      case getType(projectFilterActions.setAmount):
        draft.filters.amount = a.payload;
        return;
      case getType(projectFilterActions.setCreatedDate):
        draft.filters.createdDate = a.payload;
        return;
      case getType(projectFilterActions.setUpdatedDate):
        draft.filters.updatedDate = a.payload;
        return;
      case getType(projectFilterActions.setStatus):
        draft.filters.status = toggleRecordArrayValue(
          draft.filters.status,
          a.payload
        );
        return;
      case getType(projectFilterActions.setOpportunityType):
        draft.filters.opportunityType = toggleRecordArrayValue(
          state.filters.opportunityType,
          a.payload
        );
        return;
      case getType(projectFilterActions.setClassification):
        draft.filters.classification = toggleRecordArrayValue(
          state.filters.classification,
          a.payload
        );
        return;
      case getType(projectFilterActions.setDepartment):
        draft.filters.department = toggleRecordArrayValue(
          state.filters.department,
          a.payload
        );
        return;
      case getType(projectFilterActions.setBusinessUnit):
        draft.filters.businessUnit = toggleRecordArrayValue(
          state.filters.businessUnit,
          a.payload
        );
        return;
      case getType(projectFilterActions.setServices):
        draft.filters.services = toggleRecordArrayValue(
          state.filters.services,
          a.payload
        );
        return;

      case getType(projectFilterActions.setCustomerType):
        draft.filters.customerType = toggleRecordArrayValue(
          state.filters.customerType,
          a.payload
        );
        return;
      case getType(projectFilterActions.setFollowUpConsent):
        draft.filters.followUpConsentFilter = a.payload;
        return;
      case getType(projectFilterActions.setPromotionalConsent):
        draft.filters.promotionalConsentFilter = a.payload;
        return;
      case getType(projectFilterActions.setTeam):
        draft.filters.teamFilter = a.payload;
        draft.filters.coworkerFilter = undefined;
        return;

      case getType(projectFilterActions.setAssignedToTeam):
        draft.filters.assignedToTeam = a.payload;
        return;

      case getType(projectFilterActions.setCoworkers):
        draft.filters.coworkerFilter = a.payload;
        draft.filters.teamFilter = undefined;
        return;
      case getType(projectFilterActions.setAllFilters):
        draft.filters = {
          ...state.filters,
          coworkerFilter: undefined,
          teamFilter: undefined,
          ...a.payload,
        };
        draft.isLoaded = true;
        return;
      case getType(projectFilterActions.setTags):
        draft.filters.tags = toggleRecordArrayValue(
          state.filters.tags,
          a.payload
        );
        return;
      case getType(projectFilterActions.setNbaTypes):
        draft.filters.nbaTypes = toggleRecordArrayValue(
          state.filters.nbaTypes,
          a.payload
        );
        return;
      case getType(projectFilterActions.setOrderStatus):
        draft.filters.orderStatus = toggleRecordArrayValue(
          state.filters.orderStatus,
          a.payload
        );
        return;
      case getType(projectFilterActions.setCustomerLocales):
        draft.filters.customerLocales = toggleRecordArrayValue(
          state.filters.customerLocales,
          a.payload
        );
        return;
      case getType(projectFilterActions.setOrderMethod):
        draft.filters.orderMethod = toggleRecordArrayValue(
          state.filters.orderMethod,
          a.payload
        );
        return;
      case getType(projectFilterActions.setBokaServices):
        draft.filters.bokaServices = toggleRecordArrayValue(
          state.filters.bokaServices,
          a.payload
        );
        return;
      case getType(projectFilterActions.setInstallationQuotationStatus):
        draft.filters.installationQuotationStatus = toggleRecordArrayValue(
          state.filters.installationQuotationStatus,
          a.payload
        );
        return;
      case getType(projectFilterActions.setInstallationQuotationExists):
        draft.filters.installationQuotationExists = a.payload;
        return;
      case getType(projectFilterActions.setWorkOrderStatus):
        draft.filters.workOrderStatus = toggleRecordArrayValue(
          state.filters.workOrderStatus,
          a.payload
        );
        return;
      case getType(projectFilterActions.setWorkOrderType):
        draft.filters.workOrderType = toggleRecordArrayValue(
          state.filters.workOrderType,
          a.payload
        );
        return;
      case getType(projectFilterActions.setIsNoActionPlanned):
        draft.filters.isNoActionPlanned = a.payload;
        return;
      case getType(projectFilterActions.setProjectIds):
        draft.filters.projectIds = a.payload;
        return;
      case getType(projectFilterActions.setInstallationType):
        draft.filters.installationType = toggleRecordArrayValue(
          state.filters.installationType,
          a.payload
        );
        return;
      case getType(projectFilterActions.setInstallationDate):
        draft.filters.installationDate = a.payload;
        return;
      case getType(projectFilterActions.setIsColdProject):
        draft.filters.isColdProject = a.payload;
        return;
      case getType(projectFilterActions.setStageFilter):
        draft.filters.stageFilter = toggleRecordArrayValue(
          state.filters.stageFilter,
          a.payload
        );
        return;
      case getType(projectFilterActions.setWaitingList):
        draft.filters.waitingList = a.payload;
        return;
      case getType(projectFilterActions.setExpectedPurchaseDate):
        draft.filters.expectedPurchaseDateFilter = a.payload;
        return;
      case getType(projectFilterActions.setBudget):
        draft.filters.budgetFilter = a.payload;
        return;
    }
    assertUnreachable(a);
  });
}
